import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import apiClient from "src/apiClient"
import wsClient from "src/wsClient"
import { selectGame, selectGamePlayers, selectViewerId } from "src/store/game"
import { selectGameConfig } from "src/store/common"
import { getPlace } from "src/utils/maps"
import { wallHealthMultiplier } from "src/utils/gameModifiers"
import { anchorFor, historyFor } from "src/utils/battleHistory"


import Modal from "src/components/modals/Modal"
import BattleRoundView from "src/components/BattleRoundView"
import BattleUnitsSummary from "src/components/BattleUnitsSummary"
import FormationEditor from "src/components/FormationEditor"
import CoordChip from "src/components/CoordChip"

import "./BattleModal.css"

/* The battle as a board, the way it is fought: two armies facing each other across the front —
   special corners and the back row screened behind the line, flanks reaching around it — every
   slot a stack of one unit type, and the reserve waiting behind each side. The fill is the same
   code path the engine uses, and the formation editor writes the priorities the engine reads. */

const unitsOf = (metadata) =>
  Object.entries(metadata?.units || {})
    .map(([type, data]) => ({ type, number: data?.number ?? 0 }))
    .filter((u) => u.number > 0)


const BattleModal = ({ city, initialRound = null, tick = null }) => {
  const { t } = useTranslation()
  const game = useSelector(selectGame)
  const players = useSelector(selectGamePlayers)
  const gameConfig = useSelector(selectGameConfig)
  const userId = useSelector(selectViewerId)
  const [editing, setEditing] = useState(false)
  // Browsing the battle's own past: null follows the live round; a number pins one. The snapshots
  // come from /steps/ (fog-filtered server-side), fetched once on the first step back. A round
  // report's battle line opens the modal already pinned, via `initialRound`.
  const [viewRound, setViewRound] = useState(initialRound)
  const [steps, setSteps] = useState(null)
  const [loadingSteps, setLoadingSteps] = useState(false)
  const [showRules, setShowRules] = useState(false)

  // Opened pinned to a past round, or from a shared report line (`tick`: the round the battle
  // closed on, the only thing the chat text carries): the snapshots that hold it are needed at
  // once, not on the first ‹ press.
  useEffect(() => {
    if (initialRound == null && tick == null) return undefined
    // …unless the round asked for is already in the live payload: a battle that ended this round,
    // opened from its own report line, is whole in `battle_ended` and needs nothing fetched. It
    // is also the only way basic training's battle opens at all — that game has no server to ask.
    const liveEvents = game?.metadata?.events || []
    const here = (event) => event.coordinates?.[0] === Number(city.col) && event.coordinates?.[1] === Number(city.row)
    const inPayload =
      tick == null &&
      liveEvents.some(
        (event) =>
          here(event) &&
          // A closing with no `last_round` fought nothing this round (a missile, a famine): its
          // rounds are in the stored steps.
          ((event.type === "battle_ended" &&
            event.detail?.last_round &&
            Number(event.detail?.rounds ?? 1) === Number(initialRound)) ||
            (event.type === "battle" && Number(event.detail?.round) === Number(initialRound)))
      )
    if (inPayload) return undefined
    let cancelled = false
    setLoadingSteps(true)
    apiClient
      .get(`/game/${game.id}/steps/`)
      .then((response) => !cancelled && setSteps(response.data.steps || []))
      .catch(() => !cancelled && setSteps([]))
      .finally(() => !cancelled && setLoadingSteps(false))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const col = Number(city.col)
  const row = Number(city.row)
  const key = `${col}-${row}`
  // A battle fought on open ground — two marches meeting (Q17) — has no cell entry at all, and
  // bailing out rendered an empty modal for a report line that promised a battle. Open ground is
  // just a site with no owner and no buildings.
  const cell = game?.metadata?.cells?.[key] ?? { owner: null, metadata_by_user: {} }

  const { name } = getPlace({ row, column: col })
  const battleRound = game.metadata?.battles?.[key] ?? 0
  const rules = gameConfig?.rules_config || {}

  // Which battle this is, and the rounds of it we can read: a site hosts many battles over a
  // game and each numbers its rounds from 1, so the modal is anchored on ONE of them — the one
  // closing or burning on the round shown, or the newest closing before it — and only the steps
  // in that battle's span are its history (`utils/battleHistory`). Pouring every step's `battle`
  // event into one map keyed by round number let a replay standing on round 5 show round 21's
  // army as this battle's first round.
  const anchor = anchorFor({ game, steps, col, row, tick })
  const endedEvent = anchor?.endedEvent ?? null
  const endedTick = endedEvent ? anchor.tick : null
  const history = historyFor({ anchor, game, steps, col, row })
  // The round burning here in the payload shown, if any: the live board and its rates.
  const lastBattle =
    [...(game.metadata?.events || [])]
      .reverse()
      .find((event) => event.type === "battle" && event.coordinates[0] === col && event.coordinates[1] === row) || null
  const endedRounds = Number(endedEvent?.detail?.rounds ?? 0)
  const ended = Boolean(endedEvent) && battleRound === 0
  if (ended && endedRounds > 0 && !history[endedRounds] && endedEvent.detail?.last_round) {
    history[endedRounds] = { detail: { ...endedEvent.detail.last_round, round: endedRounds } }
  }
  const knownRounds = Object.keys(history).map(Number).sort((a, b) => a - b)
  const latestRound = knownRounds.length > 0 ? knownRounds[knownRounds.length - 1] : battleRound
  const shownRound = viewRound ?? latestRound
  // A pinned round only counts as "live" while the battle is actually burning: pinned onto a
  // finished battle, even its last round must render as history — boards and sub-rounds — not as
  // the live view's "the battle here is over" shrug. An ended battle is never live, however it
  // was opened.
  const isLive = !ended && (viewRound == null || (shownRound >= latestRound && battleRound > 0))

  const loadSteps = async () => {
    if (steps || loadingSteps) return
    setLoadingSteps(true)
    try {
      const response = await apiClient.get(`/game/${game.id}/steps/`)
      setSteps(response.data.steps || [])
    } catch {
      setSteps([])
    } finally {
      setLoadingSteps(false)
    }
  }
  const goToRound = (round) => {
    const bounded = Math.max(1, Math.min(latestRound, round))
    setViewRound(bounded >= latestRound ? null : bounded)
    if (bounded < latestRound) loadSteps()
  }

  // The middle yellow line is the wall when the defender has one: level, what it still absorbs
  // (Impenetrable included, read from the defender's own researches), and the grind so far.
  const wallInfo = (() => {
    if (cell.owner == null) return null
    const ownerMetadata = game.metadata?.users?.[String(cell.owner)]
    if (ownerMetadata?.team !== "human") return null
    const wall = cell.metadata_by_user?.[String(cell.owner)]?.buildings?.wall
    if (!wall || !wall.level) return null
    const multiplier = wallHealthMultiplier({
      gameConfig,
      team: "human",
      researchLevels: ownerMetadata?.researches || {},
    })
    // What it absorbs is Impenetrable's arithmetic, and an enemy's research is not on the wire
    // (owner, 2026-09-09) — so a defender we cannot read shows the wall it has, not a capacity
    // computed at base. The level is on the map; the number would be a guess wearing a fact's
    // clothes.
    if (!("researches" in (ownerMetadata || {}))) return { level: wall.level, total: null, remaining: null }
    const perLevel = Math.trunc((rules.WALL_HEALTH_PER_LEVEL ?? 0) * multiplier)
    const total = perLevel * wall.level
    const remaining = Math.max(0, total - (wall.damage ?? 0))
    return { level: wall.level, total, remaining }
  })()

  // Stacks standing on this very cell: an attacker arriving at a defended cell is held at the
  // gates as a *movement* (2026-08-27), so a battle's whole second team can live outside
  // `metadata_by_user`. Their compositions merge in (every stack comes whole, 36/V1).
  const stacksByUser = {}
  ;(game.metadata?.movements || []).forEach((movement) => {
    const path = movement.path || []
    if (!path.length) return
    const index = Math.min(movement.current_index ?? 0, path.length - 1)
    if (path[index][0] !== col || path[index][1] !== row) return
    const entry = (stacksByUser[movement.user_id] = stacksByUser[movement.user_id] || { units: {} })
    const known = Object.entries(movement.units || {}).filter(([, data]) => (data?.number ?? 0) > 0)
    known.forEach(([type, data]) => {
      entry.units[type] = (entry.units[type] || 0) + data.number
    })
  })

  const presentIds = [
    ...new Set([...Object.keys(cell.metadata_by_user || {}), ...Object.keys(stacksByUser)]),
  ]
  const sides = presentIds
    .map((id) => {
      const player = players.find((p) => p.user.id === Number(id))
      const units = unitsOf(cell.metadata_by_user?.[id])
      Object.entries(stacksByUser[id]?.units || {}).forEach(([type, number]) => {
        const existing = units.find((unit) => unit.type === type)
        if (existing) existing.number += number
        else units.push({ type, number })
      })
      const bodies = units.reduce((total, unit) => total + unit.number, 0)
      return { userId: id, player, team: player?.team, units, bodies }
    })
    .filter((side) => side.bodies > 0 && side.team)
  const teamsPresent = new Set(sides.map((side) => side.team))
  // The battle here closed this round and both teams still stand on the square: a stack that
  // walked in while it burned was held back (`late`) and fights next round, so a new battle is
  // brewing over the old one's report. Opened on its own, the modal is about the fight to come —
  // the army standing here, not the closing report's empty survivors (game 111, round 14:
  // handx13's 145 at 111-39 showed as nobody). The closed battle stays one step back (‹).
  const brewingAgain = ended && teamsPresent.size > 1
  const showBrewing = brewingAgain && viewRound == null

  // The summary strips: who still stands here (garrison plus stacks at the gates), and the
  // battle's running bill (the server's battle_totals while it burns; the final report's totals
  // once it ended).
  const remainingByUser = Object.fromEntries(
    sides.map((side) => [
      side.userId,
      Object.fromEntries(side.units.map((unit) => [unit.type, unit.number])),
    ])
  )
  const lossesByUser = game.metadata?.battle_totals?.[key] ?? endedEvent?.detail?.losses_by_unit ?? {}
  // The meat the horde ate, the humans who rose on its side, and the dead the medics got back up,
  // for the round shown (owner, 2026-09-07 and 2026-09-10): the live tallies while the site burns,
  // the closing report's sums on its final round, and for a browsed round the sum of every known
  // round up to it — the same rule the losses follow below. Always the server's numbers.
  const tallies = (() => {
    if (showBrewing) return { meat: {}, revived: {}, raised: {} }
    if (isLive) {
      return {
        meat: game.metadata?.battle_meat?.[key] ?? {},
        revived: game.metadata?.battle_revived?.[key] ?? {},
        raised: game.metadata?.battle_raised?.[key] ?? {},
      }
    }
    if (ended && shownRound === latestRound) {
      return {
        meat: endedEvent.detail?.meat_won ?? {},
        revived: endedEvent.detail?.revived ?? {},
        raised: endedEvent.detail?.raised ?? {},
      }
    }
    const meat = {}
    const revived = {}
    const raised = {}
    knownRounds
      .filter((r) => r <= shownRound)
      .forEach((r) => {
        Object.entries(history[r]?.detail?.meat_won || {}).forEach(([id, n]) => {
          meat[id] = (meat[id] || 0) + Number(n)
        })
        Object.entries(history[r]?.detail?.revived || {}).forEach(([id, n]) => {
          revived[id] = (revived[id] || 0) + Number(n)
        })
        Object.entries(history[r]?.detail?.raised || {}).forEach(([id, n]) => {
          raised[id] = (raised[id] || 0) + Number(n)
        })
      })
    return { meat, revived, raised }
  })()

  // The header travels with every round: for a browsed round, "standing" is that round's
  // survivors and "lost so far" the sum of every known round up to it (rounds aged out of the
  // 60-step window under-count honestly rather than inventing).
  const summaryData = showBrewing
    ? { remaining: remainingByUser, losses: {} }
    : isLive
    ? { remaining: remainingByUser, losses: lossesByUser }
    : (() => {
        const event = history[shownRound]
        if (!event) return null
        // The closing report's totals are the whole bill; the per-round events only add up to it
        // while every round is still in the window.
        if (ended && shownRound === endedRounds && endedEvent.detail?.losses_by_unit) {
          return {
            remaining: event.detail?.survivors_by_unit || endedEvent.detail?.survivors_by_unit || {},
            losses: endedEvent.detail.losses_by_unit,
          }
        }
        const cumulative = {}
        knownRounds
          .filter((r) => r <= shownRound)
          .forEach((r) => {
            Object.entries(history[r]?.detail?.losses_by_unit || {}).forEach(([id, byUnit]) => {
              const mine = (cumulative[id] = cumulative[id] || {})
              Object.entries(byUnit || {}).forEach(([unit, count]) => {
                mine[unit] = (mine[unit] || 0) + Number(count)
              })
            })
          })
        return { remaining: event.detail?.survivors_by_unit || {}, losses: cumulative }
      })()
  const myTeam = players.find((p) => p.user.id === userId)?.team
  const iFightHere = sides.some((side) => Number(side.userId) === userId)

  // What the container says on top: who holds the field once it ended, that a battle rages while
  // it burns, that one is about to when the site is contested and no round fought yet.
  const winnerTeam = ended ? endedEvent.detail?.winner_team ?? null : null
  const outcome = !ended
    ? null
    : winnerTeam == null
      ? t("game.battleReport.outcomeDraw")
      : t(`game.battleReport.outcome_${winnerTeam}`, { defaultValue: winnerTeam })
  const headline = showBrewing
    ? t("game.battle.brewing")
    : ended
    ? `⚑ ${outcome}`
    : battleRound > 0 || latestRound > 0
      ? t("game.battle.raging")
      : t("game.battle.brewing")
  const tone = showBrewing ? "live" : ended ? winnerTeam || "draw" : "live"

  // Shared, the battle becomes one team-chat line naming every seat's bill; only the site and the
  // round number travel, fog answers the rest on the reader's side (the chat's 🔎 reopens this
  // view on that round).
  const share = () => {
    const losses = summaryData?.losses || {}
    const bills = Object.keys(losses)
      .map((id) => {
        const username = players.find((p) => p.user.id === Number(id))?.user.username ?? `#${id}`
        const fallen = Object.values(losses[id] || {}).reduce((total, n) => total + Number(n), 0)
        const meat = Number(tallies.meat?.[id] ?? 0)
        return `${username} -${fallen}${meat > 0 ? ` (${t("game.battleReport.meatWon", { meat })})` : ""}`
      })
      .join(" · ")
    wsClient.send("game", {
      action: "send_chat_message",
      message: ended
        ? t("game.battleReport.sharedLine", {
            name,
            at: key,
            outcome,
            count: endedRounds || 1,
            bills,
            round: endedTick ?? game?.tick,
          })
        : t("game.battleReport.sharedLiveLine", { name, at: key, rounds: shownRound || 1, bills, round: game?.tick }),
      message_type: "team",
    })
  }

  let estimate = null
  if (lastBattle) {
    const rates = Object.entries(lastBattle.detail?.losses || {})
      .map(([id, dead]) => {
        const standing = sides.find((side) => side.userId === id)
        const alive = standing ? standing.units.reduce((total, u) => total + u.number, 0) : 0
        return dead > 0 && alive > 0 ? Math.ceil(alive / dead) : null
      })
      .filter((value) => value != null)
    if (rates.length > 0) estimate = Math.min(...rates)
  }

  // Only on your own side, only while the battle is live: the button that unfolds the editor.
  const editButtonFor = (teamKey, sideUserId) =>
    isLive && iFightHere && teamKey === myTeam && Number(sideUserId) === userId ? (
      <button type="button" className="battle-edit" onClick={() => setEditing((open) => !open)}>
        {editing ? t("game.battle.editClose") : t("game.battle.edit")}
      </button>
    ) : null

  // The heavy lifting — boards, story, sub-round table — lives in BattleRoundView, shared with
  // the final report: the same round must look the same wherever it is read.
  const roundSummary = (event) => {
    if (!event) {
      return <p className="battle-over">{loadingSteps ? t("common.loading") : t("game.battle.historyMissing")}</p>
    }
    return <BattleRoundView detail={event.detail} roundNumber={event.detail?.round} />
  }

  return (
    <Modal width="100%" isolated={false} scope="map" top="14px" maxHeight="calc(100% - 14px)">
      <div className="battle-modal with-scroll-bar vertical">
        <header className="battle-head">
          <h2>
            🔥 {t("game.battle.title", { name, at: "" }).replace(/\s*\(\s*\)\s*$/, "")}{" "}
            <CoordChip col={col} row={row} />
          </h2>
          {/* Three buttons in the corner (owner, 2026-09-07): share, help, and the sheet's own ✕
              which the base modal draws beside them. */}
          <div className="battle-head-actions">
            <button type="button" className="battle-share" onClick={share} title={t("game.battleReport.share")}>
              ⚔ {t("game.battle.share")}
            </button>
            <button
              type="button"
              className="battle-help"
              onClick={() => setShowRules((open) => !open)}
              aria-expanded={showRules}
              title={t("game.battle.rulesTitle")}
            >
              ?
            </button>
          </div>
          {/* Live notes only — the rounds-remaining estimate, the wall absorbing. An ended battle
              says nothing here: the container right below names who holds the field. */}
          {(() => {
            const notes = ended
              ? []
              : [
                  estimate != null ? t("game.battle.estimate", { count: estimate }) : null,
                  lastBattle?.detail?.wall_absorbed ? t("game.battle.wallAbsorbing") : null,
                ].filter(Boolean)
            return notes.length > 0 ? <p className="battle-meta">{notes.join(" · ")}</p> : null
          })()}
        </header>

        {showRules && (
          <div className="battle-rules">
            <p className="section-label">{t("game.battle.rulesTitle")}</p>
            <ol>
              {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                <li key={n}>
                  {t(`game.battle.rules${n}`, {
                    front: rules.BATTLEFIELD_ZONE_SLOTS?.front,
                    flanks: rules.BATTLEFIELD_ZONE_SLOTS?.flanks,
                    back: rules.BATTLEFIELD_ZONE_SLOTS?.back,
                    special: rules.BATTLEFIELD_ZONE_SLOTS?.special,
                    perRound: rules.BATTLEFIELD_SLOT_SIZE_PER_ROUND,
                    min: rules.BATTLEFIELD_SLOT_SIZE_MIN,
                  })}
                </li>
              ))}
            </ol>
          </div>
        )}
        <BattleUnitsSummary
          title={headline}
          tone={tone}
          ended={ended && !showBrewing && shownRound === latestRound}
          headerRight={
            <div className="battle-units-round-nav">
              <button
                type="button"
                className="battle-round-arrow"
                onClick={() => (showBrewing ? setViewRound(latestRound) : goToRound(shownRound - 1))}
                disabled={showBrewing ? latestRound < 1 : shownRound <= 1}
                aria-label={t("game.roundReport.older")}
              >
                ‹
              </button>
              <button
                type="button"
                className={`battle-round-label${isLive || showBrewing ? " live" : ""}`}
                onClick={() => setViewRound(null)}
                title={isLive || showBrewing ? undefined : t("game.roundReport.backToLatest")}
              >
                {t("game.battle.roundShort", { count: showBrewing ? 0 : shownRound })}
              </button>
              <button
                type="button"
                className="battle-round-arrow"
                onClick={() => (brewingAgain && shownRound >= latestRound ? setViewRound(null) : goToRound(shownRound + 1))}
                disabled={isLive || showBrewing || (!brewingAgain && shownRound >= latestRound)}
                aria-label={t("game.roundReport.newer")}
              >
                ›
              </button>
            </div>
          }
          remainingByUser={summaryData?.remaining || {}}
          lossesByUser={summaryData?.losses || {}}
          meatByUser={tallies.meat}
          raisedByUser={tallies.raised}
          revivedByUser={tallies.revived}
        />
        {showBrewing ? (
          // No board yet: the round that fights it has not been played. The last `battle` event
          // here is the closed battle's, and drawn as live it would be somebody else's fight.
          <p className="battle-over">{t("game.battle.brewingAgain")}</p>
        ) : !isLive ? (
          <>
            {/* The closing report is the summary above; below it, the final round it was settled on. */}
            {roundSummary(history[shownRound])}
          </>
        ) : (
          <>
            {teamsPresent.size < 2 && <p className="battle-over">{t("game.battle.over")}</p>}

            {wallInfo ? (
              <div
                className="battle-wall"
                role="img"
                aria-label={
                  wallInfo.total != null
                    ? t("game.battle.wallStats", wallInfo)
                    : `${t("city.buildings.wall")} ${t("city.lvl", { level: wallInfo.level })}`
                }
              >
                <div
                  className="battle-wall-fill"
                  style={{ width: `${wallInfo.total > 0 ? Math.round((wallInfo.remaining / wallInfo.total) * 100) : 0}%` }}
                />
                <span className="battle-wall-text">
                  🧱 {t("city.buildings.wall")} {t("city.lvl", { level: wallInfo.level })} ·{" "}
                  {wallInfo.total != null ? t("game.battle.wallAbsorbs", { remaining: wallInfo.remaining, total: wallInfo.total }) : null}
                </span>
              </div>
            ) : null}
            {lastBattle ? (
              <>
                <BattleRoundView
                  detail={lastBattle.detail}
                  roundNumber={lastBattle.detail?.round}
                  headerActionFor={editButtonFor}
                />
                {/* Below the field, not inside it. The board is 480px wide because its meaning is
                    positional, and on a narrow modal it scrolls sideways to stay that width — the
                    editor used to be a child of that scroller and inherited its width, so on a
                    phone a third of it sat off-screen and its own instruction was cut mid-sentence.
                    It is a control panel rather than part of the board, so out here it wraps to
                    whatever room there is. */}
                {editing ? <FormationEditor team={myTeam} onSaved={() => setEditing(false)} /> : null}
              </>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  )
}

export default BattleModal
